import axios from "axios";
import {
  NotificationsFeedBodyDocument,
  NotificationsFeedBodyQuery,
  NotificationsFeedBodyQueryVariables,
} from "graphql/_Types";
import { ExternalRegion, getEnvVar } from "ideas.env";
import { sum } from "lodash";
import { client } from "providers/ApolloProvider/ApolloProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getRequestHeaders } from "utils/getRequestHeaders";

/**
 * Fetches the unread notification count.
 * @param baseUrl Base URL used by the network request.
 * @returns The unread notification count.
 */
const getUnreadCount = async (baseUrl?: string) => {
  try {
    const url = getEnvVar("URL_NOTIFICATION_NOTIFICATION_UNREAD_COUNT")(
      baseUrl,
    );
    const headers = await getRequestHeaders();
    const res = await axios.get<{ count: number }>(url, { headers });
    return res.data.count;
  } catch (error) {
    return 0;
  }
};

/**
 * Hook that fetches the unread notification count.
 * @param scope Whether the internal region or external regions should be
 * queried.
 * @returns The unread count and refetch function.
 */
export const useNotificationUnreadCount = (scope: "internal" | "external") => {
  const [unreadCount, setUnreadCount] = useState(0);

  /**
   * Fetches the unread count from the internal region and sets the state.
   */
  const fetchCountInternal = useCallback(() => {
    void getUnreadCount().then(setUnreadCount);
  }, []);

  /**
   * Fetches the unread count from all external regions and sets the state.
   */
  const fetchCountExternal = useCallback(() => {
    const promises = getEnvVar("EXTERNAL_REGIONS").map(({ URL_DJANGO }) =>
      getUnreadCount(URL_DJANGO),
    );

    void Promise.all(promises).then((counts) => {
      const totalCount = sum(counts);
      setUnreadCount(totalCount);
    });
  }, []);

  // Derive the fetch function based on the scope
  const fetchCount = useMemo(() => {
    return scope === "internal" ? fetchCountInternal : fetchCountExternal;
  }, [fetchCountExternal, fetchCountInternal, scope]);

  // Derive the polling internal based on the scope
  const pollInterval = useMemo(() => {
    const oneMinute = 60 * 1000;
    const fiveMinutes = 5 * 60 * 1000;
    return scope === "internal" ? oneMinute : fiveMinutes;
  }, [scope]);

  // Fetch the unread count at a regular internal
  useEffect(() => {
    fetchCount();
    const timer = setInterval(fetchCount, pollInterval);
    return () => clearInterval(timer);
  }, [fetchCount, pollInterval]);

  return { unreadCount, refetch: fetchCount };
};

/**
 * Fetches the IDs of all valid notifications.
 * @param baseUrl Base URL used by the network request.
 * @returns The notification IDs.
 */
const getValidNotificationIds = async (baseUrl?: string) => {
  try {
    const url = getEnvVar("URL_NOTIFICATION_NOTIFICATION_VALID")(baseUrl);
    const headers = await getRequestHeaders();
    const res = await axios.get<{ notifications: string[] }>(url, { headers });
    return res.data.notifications;
  } catch (error) {
    return [];
  }
};

/**
 * Hook that fetches valid notifications.
 * @param scope Whether the internal region or external regions should be
 * queried.
 * @returns The notifications, loading state and error state.
 */
export const useValidNotifications = (
  scope: "internal" | "external",
  onlyShowUnread: boolean,
) => {
  const [notifications, setNotifications] =
    useState<
      NonNullable<NotificationsFeedBodyQuery["notifications"]>["nodes"]
    >();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();

  /**
   * Fetches the valid notifications from the internal region and sets the
   * state.
   */
  const fetchNotificationsInternal = useCallback(async () => {
    try {
      const notificationIds = await getValidNotificationIds();
      if (notificationIds.length > 0) {
        const { data } = await client.query<
          NotificationsFeedBodyQuery,
          NotificationsFeedBodyQueryVariables
        >({
          query: NotificationsFeedBodyDocument,
          fetchPolicy: "network-only",
          variables: {
            notificationIds,
            ...(onlyShowUnread ? { condition: { hasSeen: false } } : {}),
          },
        });

        setNotifications(data.notifications?.nodes ?? []);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      setError(error as Error);
    } finally {
      setIsLoading(false);
    }
  }, [onlyShowUnread]);

  /**
   * Fetches the valid notifications from all external regions and sets the
   * state.
   */
  const fetchNotificationsExternal = useCallback(async () => {
    try {
      const promises = getEnvVar("EXTERNAL_REGIONS").map(
        ({ URL_DJANGO, KEY }) => {
          const promise = getValidNotificationIds(URL_DJANGO).then(
            (res) => res,
          );
          return { promise, regionKey: KEY };
        },
      );

      const idsByRegionKey: Record<ExternalRegion["KEY"], string[]> = {};

      for (const region of promises) {
        const data = await region.promise;
        if (data.length > 0) {
          idsByRegionKey[region.regionKey] = data;
        }
      }

      const notifications = [];
      for (const regionKey in idsByRegionKey) {
        const region = getEnvVar("EXTERNAL_REGIONS").find(
          (region) => region.KEY === regionKey,
        );
        if (!region) continue;
        const { data } = await client.query<
          NotificationsFeedBodyQuery,
          NotificationsFeedBodyQueryVariables
        >({
          query: NotificationsFeedBodyDocument,
          fetchPolicy: "network-only",
          variables: {
            notificationIds: idsByRegionKey[regionKey],
            ...(onlyShowUnread ? { condition: { hasSeen: false } } : {}),
          },
          context: {
            uri: getEnvVar("EXTERNAL_REGIONS").find(
              (region) => region.KEY === regionKey,
            )?.URL_GRAPHQL,
            queryDeduplication: false,
          },
        });
        notifications.push(
          ...(data.notifications?.nodes.map((notification) => {
            return {
              ...notification,
              region,
            };
          }) ?? []),
        );
      }

      setNotifications(notifications);
    } catch (error) {
      setError(error as Error);
    } finally {
      setIsLoading(false);
    }
  }, [onlyShowUnread]);

  // Derive the fetch function based on the scope
  const fetchNotifications = useMemo(() => {
    return scope === "internal"
      ? fetchNotificationsInternal
      : fetchNotificationsExternal;
  }, [fetchNotificationsExternal, fetchNotificationsInternal, scope]);

  // Derive the polling internal based on the scope
  const pollInterval = useMemo(() => {
    const oneMinute = 60 * 1000;
    const fiveMinutes = 5 * 60 * 1000;
    return scope === "internal" ? oneMinute : fiveMinutes;
  }, [scope]);

  // Fetch the valid notifications at a regular interval
  useEffect(() => {
    setIsLoading(true);
    void fetchNotifications();
    const timer = setInterval(() => void fetchNotifications(), pollInterval);
    return () => clearInterval(timer);
  }, [fetchNotifications, pollInterval]);

  return {
    notifications,
    isLoading,
    error,
    refetch: fetchNotifications,
  };
};
